import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Calendar,
  Briefcase,
  Smartphone,
  Settings,
  Code2,
  Key,
  Bot,
  Sliders,
  ChevronDown,
  ChevronRight,
  LogOut,
  X,
  PanelLeftClose,
  FileText,
  Map,
  Activity,
  Network,
  Gauge,
  Lock,
  Palette,
  ClipboardList,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { useTheme, type ThemePref } from '../context/ThemeContext';
import { meAPI, type ModuleInfo } from '../services/api';
import { ROLE_LABEL, userRoles, type SaasRole } from '../utils/roles';

type Role = SaasRole;
type ModuleKey = 'prospect' | 'agendamento' | 'central';

interface LeafItem {
  type: 'leaf';
  to: string;
  label: string;
  icon: React.ReactNode;
  /**
   * Perfis com acesso. NÃO esconde o item — todo usuário enxerga o menu
   * inteiro; quem não tem o perfil vê o cadeado e cai no "Acesso negado".
   */
  roles?: Role[];
  /** Some do menu se o módulo não está em MODULES_ENABLED (licença do tenant). */
  requireModule?: ModuleKey;
}

interface SectionItem {
  type: 'section';
  id: string;
  label: string;
  icon: React.ReactNode;
  /** Prefixo pra auto-expandir quando a rota ativa bate. */
  pathPrefix: string;
  requireModule?: ModuleKey;
  /** Seção só pode ter leaves (1 nível de aninhamento). */
  children: LeafItem[];
}

type NavNode = LeafItem | SectionItem;

/**
 * Menu principal (Fase 33): módulos promovidos ao primeiro nível — sem o
 * agrupador "Módulos". Todos os itens são visíveis para qualquer perfil;
 * o controle de acesso continua nas rotas (ProtectedRoute).
 */
const NAV: NavNode[] = [
  {
    type: 'leaf',
    to: '/',
    label: 'Dashboard',
    icon: <LayoutDashboard size={18} />,
  },
  {
    type: 'section',
    id: 'agendamento',
    label: 'Agendamento',
    icon: <Calendar size={18} />,
    pathPrefix: '/agendamento',
    requireModule: 'agendamento',
    children: [
      {
        type: 'leaf',
        to: '/agendamento/agenda',
        label: 'Agenda',
        icon: <Calendar size={16} />,
        roles: ['admin', 'operator', 'monitor'],
      },
      {
        type: 'leaf',
        to: '/agendamento/ordens-servico',
        label: 'Ordens de Serviço',
        icon: <ClipboardList size={16} />,
        roles: ['admin', 'operator', 'monitor'],
      },
      {
        type: 'leaf',
        to: '/agendamento/monitoramento',
        label: 'Monitoramento',
        icon: <Activity size={16} />,
        roles: ['admin', 'operator', 'monitor'],
      },
      {
        type: 'leaf',
        to: '/agendamento/configuracoes',
        label: 'Configurações',
        icon: <Settings size={16} />,
        roles: ['admin', 'monitor'],
      },
    ],
  },
  {
    type: 'section',
    id: 'prospect',
    label: 'Prospect',
    icon: <Briefcase size={18} />,
    pathPrefix: '/prospect',
    requireModule: 'prospect',
    children: [
      {
        type: 'leaf',
        to: '/prospect/dashboard',
        label: 'Dashboard',
        icon: <LayoutDashboard size={16} />,
        roles: ['admin', 'operator', 'gestao_vendas', 'vendedor'],
      },
      {
        type: 'leaf',
        to: '/prospect/followup',
        label: 'Follow Up',
        icon: <Activity size={16} />,
        roles: ['admin', 'operator', 'gestao_vendas'],
      },
      {
        type: 'leaf',
        to: '/prospect/launched',
        label: 'Contratos',
        icon: <FileText size={16} />,
        roles: ['admin', 'operator', 'gestao_vendas', 'vendedor'],
      },
      {
        type: 'leaf',
        to: '/prospect/map',
        label: 'Mapa',
        icon: <Map size={16} />,
        roles: ['admin', 'gestao_vendas'],
      },
      {
        type: 'leaf',
        to: '/prospect/settings',
        label: 'Configurações',
        icon: <Settings size={16} />,
        roles: ['admin', 'gestao_vendas'],
      },
    ],
  },
  {
    type: 'leaf',
    to: '/central',
    label: 'Central do Cliente',
    icon: <Smartphone size={18} />,
    requireModule: 'central',
  },
  {
    type: 'leaf',
    to: '/rede',
    label: 'Rede',
    icon: <Network size={18} />,
    // Sem `roles` enquanto a página é placeholder — definir ao implementar.
  },
  {
    type: 'leaf',
    to: '/qualidade',
    label: 'Qualidade',
    icon: <Gauge size={18} />,
  },
  {
    type: 'section',
    id: 'configuracoes',
    label: 'Configurações',
    icon: <Settings size={18} />,
    pathPrefix: '/configuracoes',
    children: [
      {
        type: 'leaf',
        to: '/configuracoes/usuarios',
        label: 'Usuários',
        icon: <Users size={16} />,
        roles: ['admin', 'operator'],
      },
      {
        type: 'leaf',
        to: '/configuracoes/aplicacao',
        label: 'Aplicação',
        icon: <Code2 size={16} />,
        roles: ['admin', 'operator'],
      },
      {
        type: 'leaf',
        to: '/configuracoes/token',
        label: 'Token ERP',
        icon: <Key size={16} />,
        roles: ['admin', 'operator'],
      },
      {
        type: 'leaf',
        to: '/configuracoes/ia',
        label: 'IA Token',
        icon: <Bot size={16} />,
        roles: ['admin', 'operator'],
      },
      {
        type: 'leaf',
        to: '/configuracoes/parametrizacao',
        label: 'Parametrização',
        icon: <Sliders size={16} />,
        roles: ['admin', 'operator'],
      },
      {
        type: 'leaf',
        to: '/billing',
        label: 'Assinatura',
        icon: <CreditCard size={16} />,
        roles: ['admin', 'operator'],
      },
      {
        type: 'leaf',
        to: '/design-system',
        label: 'Design system',
        icon: <Palette size={16} />,
        roles: ['admin', 'operator'],
      },
    ],
  },
];

export default function Sidebar({
  onNavigate,
  onCollapse,
}: {
  onNavigate?: () => void;
  /** Desktop: recolhe a sidebar (botão hambúrguer no Layout reabre) */
  onCollapse?: () => void;
}) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { clientName, logoSrc } = useBranding();
  const [enabledModules, setEnabledModules] = useState<ModuleInfo[]>([]);
  const [loadingModules, setLoadingModules] = useState(true);

  useEffect(() => {
    meAPI
      .modules()
      .then((r) => setEnabledModules(r.enabled))
      .catch(() => setEnabledModules([]))
      .finally(() => setLoadingModules(false));
  }, []);

  const myRoles = userRoles(user);
  const enabledIds = new Set(enabledModules.map((m) => m.id as ModuleKey));

  /** Módulo contratado pelo tenant? (única regra que ainda esconde item) */
  function isAvailable(item: { requireModule?: ModuleKey }): boolean {
    return !item.requireModule || enabledIds.has(item.requireModule);
  }

  /** Usuário tem perfil pra abrir? Sem `roles` = livre pra todo mundo. */
  function canOpen(item: { roles?: Role[] }): boolean {
    if (!item.roles) return true;
    return item.roles.some((r) => myRoles.includes(r));
  }

  async function handleLogout() {
    if (!confirm('Encerrar sessão?')) return;
    await logout();
  }

  return (
    <>
      <div className="px-5 pt-5 pb-3 border-b border-line flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={clientName}
              className="h-11 w-auto max-w-[3rem] object-contain flex-shrink-0"
            />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-primary-solid flex items-center justify-center font-bold text-white flex-shrink-0">
              {clientName.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-base font-bold text-fg truncate">{clientName}</div>
            <div className="text-xs text-muted">Painel admin</div>
          </div>
        </div>
        {onNavigate && (
          <button
            onClick={onNavigate}
            className="md:hidden text-fg-2"
            aria-label="Fechar menu"
          >
            <X size={20} />
          </button>
        )}
        {onCollapse && (
          <button
            onClick={onCollapse}
            className="hidden md:flex w-8 h-8 rounded-lg hover:bg-hover items-center justify-center text-fg-2 hover:text-fg transition"
            aria-label="Recolher menu"
            title="Recolher menu"
          >
            <PanelLeftClose size={18} />
          </button>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map((node) => {
          if (!isAvailable(node)) return null;
          if (node.type === 'leaf') {
            return (
              <NavLeaf
                key={node.to}
                item={node}
                level="root"
                locked={!canOpen(node)}
                onNavigate={onNavigate}
              />
            );
          }
          const visibleChildren = node.children.filter(isAvailable);
          if (visibleChildren.length === 0) return null;
          return (
            <Section
              key={node.id}
              node={node}
              currentPath={location.pathname}
              visibleChildren={visibleChildren}
              onNavigate={onNavigate}
              canOpen={canOpen}
            />
          );
        })}

        {loadingModules && (
          <div className="px-3 py-2 text-xs text-faint italic">Carregando módulos…</div>
        )}
      </nav>

      <div className="border-t border-line p-3">
        <ThemeSwitcher />
        {user && (
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-medium text-fg truncate">{user.name}</div>
            <div className="text-xs text-muted truncate">{user.email}</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {userRoles(user).map((r) => (
                <span
                  key={r}
                  className="inline-block px-2 py-0.5 rounded-full bg-primary-soft/50 border border-primary-line/50 text-xs text-primary"
                >
                  {ROLE_LABEL[r] ?? r}
                </span>
              ))}
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-danger hover:bg-danger-soft/20 transition"
        >
          <LogOut size={18} />
          <span>Sair</span>
        </button>
      </div>
    </>
  );
}

/**
 * Item de menu. `locked` só muda a aparência (cadeado + opacidade) — o link
 * continua clicável e a rota é quem barra, mantendo uma mensagem clara.
 */
function NavLeaf({
  item,
  level,
  locked,
  onNavigate,
}: {
  item: LeafItem;
  level: 'root' | 'child';
  locked: boolean;
  onNavigate?: () => void;
}) {
  const base =
    level === 'root'
      ? 'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition'
      : 'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition';
  const idle =
    level === 'root'
      ? 'text-fg-2 hover:bg-hover'
      : 'text-fg-2 hover:bg-hover hover:text-fg';

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={onNavigate}
      title={locked ? 'Você não tem permissão para acessar' : undefined}
      className={({ isActive }) =>
        `${base} ${isActive ? 'bg-primary-solid text-white' : idle} ${locked ? 'opacity-50' : ''}`
      }
    >
      {item.icon}
      <span className="flex-1">{item.label}</span>
      {locked && <Lock size={12} className="flex-shrink-0 opacity-70" />}
    </NavLink>
  );
}

function ThemeSwitcher() {
  const { theme, setTheme } = useTheme();
  const options: Array<{ value: ThemePref; label: string; icon: React.ReactNode }> = [
    { value: 'light', label: 'Claro', icon: <Sun size={14} /> },
    { value: 'dark', label: 'Escuro', icon: <Moon size={14} /> },
    { value: 'system', label: 'Sistema', icon: <Monitor size={14} /> },
  ];
  return (
    <div className="mb-2 grid grid-cols-3 gap-1 rounded-lg bg-canvas border border-line p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => setTheme(o.value)}
          title={o.label}
          className={`flex items-center justify-center gap-1 rounded-md px-1 py-1.5 text-xs transition ${
            theme === o.value
              ? 'bg-primary-solid text-white'
              : 'text-fg-2 hover:bg-hover hover:text-fg'
          }`}
        >
          {o.icon}
          <span className="hidden lg:inline">{o.label}</span>
        </button>
      ))}
    </div>
  );
}

function Section({
  node,
  currentPath,
  visibleChildren,
  onNavigate,
  canOpen,
}: {
  node: SectionItem;
  currentPath: string;
  visibleChildren: LeafItem[];
  onNavigate?: () => void;
  canOpen: (item: { roles?: Role[] }) => boolean;
}) {
  const isPathActive =
    currentPath.startsWith(node.pathPrefix) ||
    visibleChildren.some((c) => currentPath === c.to || currentPath.startsWith(c.to + '/'));
  const [open, setOpen] = useState(isPathActive);
  // Seção inteira bloqueada = nenhum filho acessível.
  const allLocked = visibleChildren.every((c) => !canOpen(c));

  useEffect(() => {
    if (isPathActive) setOpen(true);
  }, [isPathActive]);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
          isPathActive ? 'text-primary bg-hover/50' : 'text-fg-2 hover:bg-hover'
        } ${allLocked ? 'opacity-50' : ''}`}
      >
        {node.icon}
        <span className="flex-1 text-left">{node.label}</span>
        {allLocked && <Lock size={12} className="opacity-70" />}
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <div className="ml-3 mt-1 border-l border-line pl-2 space-y-0.5">
          {visibleChildren.map((c) => (
            <NavLeaf
              key={c.to}
              item={c}
              level="child"
              locked={!canOpen(c)}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
